"use client";

import { useState, useTransition } from "react";
import { useLocale, useTranslations } from "next-intl";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { cn } from "@/lib/utils";
import { Textarea } from "@/components/ui/textarea";
import {
  sendSupportMessage,
  startSupportConversation,
} from "@/app/actions/support";
import type { LangCode } from "@/types/message";
import type { SupportConversation, SupportMessage } from "@/types/support";

interface SupportComposerProps {
  conversation: SupportConversation | null;
  /** 키보드가 홈 인디케이터를 덮는 동안 safe-area 패딩을 떨어뜨린다 —
   *  유지하면 입력창이 키보드 위에 떠서 여백이 보인다. */
  keyboardOpen?: boolean;
  onConversationCreated: (conv: SupportConversation) => void;
  onMessageSent: (msg: SupportMessage) => void;
}

const MAX_LEN = 4000;

export function SupportComposer({
  conversation,
  keyboardOpen = false,
  onConversationCreated,
  onMessageSent,
}: SupportComposerProps) {
  const t = useTranslations("support.composer");
  const tErrors = useTranslations("support.errors");
  const locale = useLocale() as LangCode;

  const [text, setText] = useState("");
  const [isPending, startTransition] = useTransition();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const value = text.trim();
    if (!value || isPending) return;
    if (value.length > MAX_LEN) {
      toast.error(t("tooLong"));
      return;
    }

    startTransition(async () => {
      // Lazily create the conversation on first message.
      let activeConv = conversation;
      if (!activeConv) {
        const res = await startSupportConversation({
          locale,
          lastSeenUrl:
            typeof window !== "undefined"
              ? window.location.pathname
              : undefined,
        });
        if (!res.data) {
          toast.error(tErrors(res.error ?? "UNEXPECTED_ERROR"));
          return;
        }
        activeConv = res.data;
        onConversationCreated(activeConv);
      }

      const sendRes = await sendSupportMessage({
        conversation_id: activeConv.id,
        content: value,
        original_lang_code: locale,
        // Always auto-translate to KO when the customer is on a non-KO locale
        // so super_admin sees the message in Korean without extra friction.
        translate: locale !== "ko",
      });
      if (!sendRes.data) {
        toast.error(tErrors(sendRes.error ?? "UNEXPECTED_ERROR"));
        return;
      }
      onMessageSent(sendRes.data);
      setText("");
    });
  };

  return (
    <form
      onSubmit={handleSubmit}
      // Aligns horizontally with the message list (px-3) and follows the same
      // safe-area pattern as the 1:1 chat composer in chat-room-page-client.
      className="bg-gray-50 px-3 pt-2"
      style={{
        paddingBottom: keyboardOpen
          ? "0.5rem"
          : "calc(0.5rem + var(--loc-safe-area-inset-bottom))",
      }}
    >
      {/* Both controls share h-9 (36px) and use items-stretch for a balanced rail. */}
      <div className="flex items-stretch gap-1.5">
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (
              e.key === "Enter" &&
              !e.shiftKey &&
              !e.nativeEvent.isComposing &&
              e.keyCode !== 229
            ) {
              e.preventDefault();
              handleSubmit(e);
            }
          }}
          placeholder={t("placeholder")}
          rows={1}
          // overflow-hidden suppresses the textarea's default vertical
          // scrollbar — we treat this as a single-line input so a
          // scrollbar is never useful.
          className={cn(
            "h-9 min-h-9 flex-1 bg-white resize-none overflow-hidden rounded-full px-3.5 py-1.5 text-sm leading-6",
            text.length > MAX_LEN && "border-red-400",
          )}
          maxLength={MAX_LEN + 100}
        />
        <button
          type="submit"
          disabled={!text.trim() || isPending}
          aria-label={t("send")}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-orange-500 text-white transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Send className="h-4 w-4" />
        </button>
      </div>
    </form>
  );
}
